import { useTranslation } from "react-i18next";
import { Box, Button, Divider } from "@mui/material";

import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

import { Service } from "../../types/service.type";
import { useRequestService } from "../../api/mutations/useRequestService";
import { useCancelServiceRequest } from "../../api/mutations/useCancelServiceRequest";
import { useUpdateServiceStatus } from "../../api/mutations/useUpdateServiceStatus";
import { useDeleteService } from "../../api/mutations/useDeleteService";

import { styles } from "./styles";
import { ShareService } from "./ShareService";
import { ServiceHeader } from "./ServiceHeader";
import { ServiceDetails } from "./ServiceDetails";
import { ProviderDetails } from "./ProviderDetails";
import { ServiceSummaryLoader } from "./ServiceSummaryLoader";
import useIsOwn from "../../../../hooks/useIsOwn";
import { CancelModal, useCancelModal } from "../CancelModal";
import { ServiceStatusEnum } from "../../enum/service.enum";

const ServiceSummary = ({
  service,
  isServiceLoading,
  handleRequestSubmittedModal,
}: {
  service?: Service;
  isServiceLoading: boolean;
  handleRequestSubmittedModal: () => void;
}) => {
  if (isServiceLoading) {
    return <ServiceSummaryLoader />;
  }

  if (!isServiceLoading && !service) return null;

  return <ServiceSummaryContent service={service!} handleRequestSubmittedModal={handleRequestSubmittedModal} />;
};

const ServiceSummaryContent = ({
  service,
  handleRequestSubmittedModal,
}: {
  service: Service;
  handleRequestSubmittedModal: () => void;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });
  const { openCancelModal, handleOpenCancelModal, handleCloseCancelModal } = useCancelModal();
  const {
    openCancelModal: openDeleteModal,
    handleOpenCancelModal: handleOpenDeleteModal,
    handleCloseCancelModal: handleCloseDeleteModal,
  } = useCancelModal();

  const {
    id,
    completedJobs = 0,
    availability,
    customerPreferenceConfig,
    paymentConfig,
    type,
    expiryDate,
    user,
    status,
  } = service;

  const { amount, currency, type: paymentType, unit } = paymentConfig;

  const serviceLocations = availability?.locations ?? [];
  const isOnline = Boolean(serviceLocations.length && serviceLocations.length > 0);
  const ageRange = customerPreferenceConfig?.ageRange;
  const { fullName, firstTwoChr, media, club, role, isVerified, profileLink } = useUserDataFormatter(user);

  const { mutate: requestService } = useRequestService(id, {
    onSuccess: handleRequestSubmittedModal,
  });

  const canRequestService = service?.canRequest;

  const handleRequestService = () => {
    if (!canRequestService) return;
    requestService();
  };

  const { mutate: cancelServiceRequest } = useCancelServiceRequest(service?.requestId!);

  const handleCancelServiceRequest = () => {
    if (!service?.requestId) return;
    cancelServiceRequest();
    handleCloseCancelModal();
  };

  const { isOwn: isOwnService, isLoading: isOwnServiceLoading } = useIsOwn({
    userId: service?.user?.id,
    isUserDataLoading: false,
  });

  const isLoading = isOwnServiceLoading;

  const canControlServiceStatus = isOwnService && service.approvedByAdmin;

  const { mutate: updateServiceStatus, isPending: isUpdatingService } = useUpdateServiceStatus(id);

  const handleToggleServiceStatus = () => {
    const newStatus = status === ServiceStatusEnum.Published ? ServiceStatusEnum.Draft : ServiceStatusEnum.Published;
    updateServiceStatus({ status: newStatus });
  };

  const { mutate: deleteService, isPending: isDeletingService } = useDeleteService(id);

  const handleRemoveService = () => {
    handleOpenDeleteModal();
  };

  const handleConfirmDeleteService = () => {
    deleteService();
    handleCloseDeleteModal();
  };

  return (
    <Box sx={styles.container}>
      <ServiceHeader
        amount={amount}
        currency={currency}
        type={type}
        expiryDate={expiryDate ?? undefined}
        paymentType={paymentType}
        unit={unit}
      />
      <Divider />
      <ServiceDetails
        completedJobs={completedJobs}
        isOnline={isOnline}
        ageRange={ageRange}
        serviceLocations={serviceLocations}
      />
      <Divider />
      <ProviderDetails
        profileLink={profileLink}
        fullName={fullName}
        firstTwoChr={firstTwoChr}
        media={media ?? undefined}
        club={club}
        role={role}
        isVerified={isVerified}
      />
      <Divider />
      {!isOwnService && canRequestService && (
        <>
          <Button onClick={handleRequestService} disabled={isLoading} variant="primary-md">
            {t("requestThisService")}
          </Button>
          <Divider />
        </>
      )}
      {!isOwnService && !canRequestService && (
        <>
          <Button onClick={handleOpenCancelModal} disabled={isLoading} variant="outlined-md">
            {t("cancelRequest")}
          </Button>
          <Divider />
        </>
      )}
      {canControlServiceStatus && status === ServiceStatusEnum.Published && (
        <Button onClick={handleToggleServiceStatus} disabled={isLoading || isUpdatingService} variant="outlined-md">
          {t("pauseThisService")}
        </Button>
      )}
      {canControlServiceStatus && status === ServiceStatusEnum.Draft && (
        <Button onClick={handleToggleServiceStatus} disabled={isLoading || isUpdatingService} variant="outlined-md">
          {t("publishThisService")}
        </Button>
      )}
      {isOwnService && (
        <Button
          onClick={handleRemoveService}
          disabled={isLoading || isDeletingService}
          variant="outlined-md"
          sx={styles.removeServiceButton}
        >
          {t("removeThisService")}
        </Button>
      )}
      <ShareService />

      <CancelModal
        open={openCancelModal}
        closeModal={handleCloseCancelModal}
        handelCancel={handleCancelServiceRequest}
        title={t("areYouSureToCancelThisRequest")}
      />
      <CancelModal
        open={openDeleteModal}
        closeModal={handleCloseDeleteModal}
        handelCancel={handleConfirmDeleteService}
        title={t("areYouSureToRemoveThisService")}
      />
    </Box>
  );
};

export default ServiceSummary;
